import { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';

import { fetch_one, fetch_all } from '../../lib/server_helper';
import { loginAtom } from '../../lib/auth';

import {
  Box,
  CircularProgress,
  Divider,
  Grid
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';

import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EmailIcon from '@mui/icons-material/Email';
import EuroIcon from '@mui/icons-material/Euro';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { AlertCheckInputs } from '../../components/alerts/CheckInputs';
import { AlertSaved } from '../../components/alerts/Saved';
import { AlertSomethingWentWrong } from '../../components/alerts/SomethingWentWrong';

import {
  DataGrid,
  Page,
  StyledButton,
  StyledIconButton,
  StyledSelect,
  StyledTextField,
  Typography
} from '../../components/interface';

import { Emission } from '../../models/Emission';
import { EmissionKey } from '../../models/EmissionKey';
import { User } from '../../models/User';

const { DateTime } = require("luxon");

type Props = {};

export const EmissionChecklist: FunctionComponent<Props> = () => {
  // const navigate = useNavigate();
  const theme = useTheme();

  const [login, setLogin] = useAtom(loginAtom);
  const [emissions, setEmissions] = useState<Emission[]>([]);
  const [handlers, setHandlers] = useState<{id: string, name: string}[]>([]);
  const [emissionKeys, setEmissionKeys] = useState<EmissionKey[]>([]);
  const [activeKey, setActiveKey] = useState<EmissionKey>();
  const [objectsCount, setObjectsCount] = useState<number>(0);
  const [loader, setLoader] = useState<boolean>(true);
  const [removeLoader, setRemoveLoader] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [order, setOrder] = useState<string>("updated_at DESC");

  const [errorSnackOpen, setErrorSnackOpen] = useState<boolean>(false);
  const [serverErrorSnackOpen, setServerErrorSnackOpen] = useState<boolean>(false);
  const [exitSavedSnackOpen, setExitSavedSnackOpen] = useState<boolean>(false);

  const [search, setSearch] = useState<string>("");
  const [searchEmission, setSearchEmission] = useState<number>();
  const [searchHandler, setSearchHandler] = useState<number>(0);
  const [enterPressed, setEnterPressed] = useState(false);
  const [filterProfile, setFilterProfile] = useState(99);
  const [filterBorderel, setFilterBorderel] = useState(99);
  const [filterPayment, setFilterPayment] = useState(99);
  const [filterVerified, setFilterVerified] = useState(0);
  const [filterEmail, setFilterEmail] = useState(99);
  const [filterDeleted, setFilterDeleted] = useState(0);
  const [filterGifts, setFilterGifts] = useState(99);

  useEffect(() => {
    fetch_all<Emission>('/nl/v3/objects/fetch_all', {
      object: 'emission',
      fields: ['id', 'start_date', 'end_date'],
      per_page: 20,
      page: 0,
      order: "start_date DESC"
    }, (obs, complete_data) => {
      setEmissions(obs);
      setSearchEmission(obs[0].id);
    }, (z) => {

    }, login);
  }, []);

  useEffect(() => {
    fetch_one<Emission>('/nl/v3/objects/custom_action', {
      object: 'emission',
      class_action: 'custom_api_get_handlers'
    }, (ob, complete_data) => {
      setHandlers(ob.custom_result?.users || []);
      setSearchEmission(parseInt((ob.custom_result?.users || []).filter(o => o.id === (login?.id || '').toString())[0].id, 10) || 0);
    }, (z) => {

    }, login);
  }, []);

  useEffect(() => {
    if (!!searchEmission) {
      setLoader(true);
      fetch_all<EmissionKey>('/nl/v3/objects/fetch_all', {
        object: 'emission_key',
        fields: [
          'id', 'created_at', 'updated_at', 'asn_key', 'user|id/full_name/stock_number/vennoot_number', 'proposed_value', 'proposed_amount',
          'activated', 'is_verified', 'flag_email', 'handler|full_name', 'extra_data',
          'check_profile', 'check_profile_updated_at', 'check_profile_confirmed_at',
          'check_borderel', 'check_borderel_updated_at', 'check_borderel_confirmed_at',
          'check_payment', 'check_payment_updated_at', 'check_payment_confirmed_at',
          'emails|last|created_at', 'user|extra_data', 'is_error', 
          'gifter|id/full_name/stock_number/vennoot_number', 'gifter|extra_data', 'gift_expiry_date'
        ],
        per_page: 20,
        page: page,
        order: order,
        filter: {
          search: '',
          advanced: {
            checklist: 1,
            emission_id: searchEmission,
            handler_id: searchHandler,
            catch_all: search,
            filter_profile: filterProfile,
            filter_borderel: filterBorderel,
            filter_payment: filterPayment,
            filter_verified: filterVerified,
            filter_email: filterEmail,
            filter_deleted: filterDeleted,
            filter_gift: filterGifts
          }
        }
      }, (obs, complete_data) => {
        setEmissionKeys(obs);

        let count:number = 0;
        if (typeof(complete_data?.data) === 'object' && 'count' in complete_data?.data) count = complete_data?.data?.count;
        setObjectsCount(count);
  
        setLoader(false);
      }, (z) => {
  
      }, login);
    }
  }, [search, searchEmission, searchHandler, order, page, filterProfile, filterBorderel, filterPayment, filterVerified, filterEmail, filterDeleted, filterGifts]);

  const renderFilter = (Icon: React.ComponentType<{
    sx?: any;
    onClick?: () => void
  }>, name: string, active: number, setActive: (n:number) => void, include_red: boolean, include_gold: boolean) => {
    return (<Box sx={{
      border: '2px solid transparent',
      borderColor: theme.palette.primary.main,
      textAlign: 'center'
    }}>
      <Box sx={{
        borderBottom: '2px solid transparent',
        borderColor: theme.palette.primary.main,
        paddingTop: 0.5,
        paddingBottom: 0.5
      }}>
        <Typography>{name}</Typography>
      </Box>
      <Box sx={{
        paddingTop: 0.5,
        paddingBottom: 0.5
      }}>
        <Icon
          sx={{height: '32px', width: 'auto', color: 'black', cursor: 'pointer', opacity: active === 99 ? 1 : 0.3}}
          onClick={() => {
            setActive(99);
          }}
        />
        {!!include_red && <Icon
          sx={{height: '32px', width: 'auto', color: 'red', cursor: 'pointer', opacity: active === 0 ? 1 : 0.3}}
          onClick={() => {
            setActive(0);
          }}
        />}
        {!!include_gold && <Icon
          sx={{height: '32px', width: 'auto', color: 'gold', cursor: 'pointer', opacity: active === 2 ? 1 : 0.3}}
          onClick={() => {
            setActive(2);
          }}
        />}
        <Icon
          sx={{height: '32px', width: 'auto', color: 'green', cursor: 'pointer', opacity: active === 1 ? 1 : 0.3}}
          onClick={() => {
            setActive(1);
          }}
        />
      </Box>
    </Box>);
  }

  const columns: GridColDef[] = [
    { field: 'vennoot_number', headerName: 'VenNR', width: 120 },
    // { field: 'user_full_name', headerName: 'Name', width: 240 },
    { field: 'user_full_name', headerName: 'Name', width: 240, renderCell: params => <>
      {!params.row.extra_data?.gift && <Typography sx={{fontSize: '0.9rem'}}>{params.row.user_full_name}</Typography>}
      {!!params.row.extra_data?.gift && <>
        <Typography sx={{position: 'relative', width: '100%'}}>
          <span>{params.row.user_full_name || params.row.extra_data?.gift_data?.first_name}</span><br/>
          <span style={{fontSize: '0.7rem', marginTop: -6, display: 'block'}}>{params.row.gifter_full_name}</span>
          <CardGiftcardIcon sx={{
            color: theme.palette.tertiary?.main,
            position: 'absolute',
            top: 4,
            right: 0
          }} />
        </Typography>
      </>}
    </> },
    { field: 'icons', headerName: 'States', width: 140, renderCell: params => <>
      {!!params.row.user_extra_data?.itsme_registered && <>
        <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" style={{
          width: '24px', 
          marginBottom: -0.5, 
          color: !!params.row.check_profile ? 
          'green' : 
          (
            (!!params.row.check_profile_updated_at && (!params.row.check_profile_confirmed_at || (params.row.check_profile_updated_at > params.row.check_profile_confirmed_at))) ? 
            'gold' : 
            'red'
          )
        }}><path d="M4.28 57.34C.91 53.97-.837 47.446.396 42.844l6.728-25.108c1.233-4.603 6.01-9.38 10.613-10.613L42.844.395C47.446-.838 53.97.91 57.34 4.28l18.38 18.38c3.37 3.362 5.116 9.893 3.894 14.495l-6.737 25.108c-1.233 4.603-6.01 9.38-10.613 10.613l-25.108 6.737C32.554 80.847 26.03 79.1 22.66 75.73L4.28 57.34z" fill="currentColor"></path><path d="M49.094 28.807c-2.389-.816-3.906-1.13-3.798-2.545.084-1.103.92-1.67 2.453-1.544 1.118.086 1.795.549 2.136 1.246a2.647 2.647 0 002.323 1.473c2.227.049 3.584-2.538 2.188-4.276-1.255-1.562-3.32-2.618-6.23-2.84-5.11-.393-8.33 1.892-8.63 5.782-.32 4.166 2.48 5.293 6.72 6.724 2.895.933 3.612 1.385 3.528 2.602-.09 1.18-1.125 1.765-2.697 1.652-1.31-.101-2.115-.62-2.494-1.398-.426-.882-1.38-1.37-2.354-1.39a2.617 2.617 0 00-1.947.786s-1.102 1.233-3.253 1.233c-1.563-.042-2.948-.883-2.948-3.072l.053-7.322a3.138 3.138 0 101.632-5.81 3.056 3.056 0 00-1.597.464l.004-2.689a1.717 1.717 0 00-1.715-1.726h-2.33a1.716 1.716 0 00-1.715 1.707l-.102 15.72c-.152 1.956-1.504 2.708-3.017 2.719-1.605 0-3.047-.84-3.047-3.072l.108-9.925a1.134 1.134 0 00-1.134-1.142h-3.487a1.134 1.134 0 00-1.133 1.125l-.11 11.327c-.04 4.721 3.68 6.845 8.803 6.845 2.144 0 3.615-.864 4.632-2.066 1.3 1.404 3.332 2.066 5.791 2.066 2.459 0 4.298-.322 6.124-1.358 1.27.747 2.89 1.215 4.852 1.365 5.306.408 8.535-1.985 8.853-6.122.277-3.69-2.495-5.17-6.462-6.54zM19.628 19.65a3.138 3.138 0 10-3.127-3.11 3.13 3.13 0 003.127 3.11zm36.84 23.117c-5.142 0-9.002 2.893-10.065 7.778-.29-4.576-3.209-7.32-7.511-7.32-3.002 0-4.965 1.348-6.118 3.156-1.31-2.03-3.502-3.156-6.235-3.156-2.536 0-10.544-.308-10.544 9.98v7.489a2.81 2.81 0 105.618 0v-8.226c0-2.386 1.347-4 3.579-4 2.04 0 3.23 1.422 3.23 3.97v8.271a2.81 2.81 0 005.62 0v-8.933c.22-2 1.462-3.309 3.462-3.309 2.155 0 3.309 1.423 3.309 3.97v8.273a2.81 2.81 0 105.617 0v-4.398c1.102 4.794 4.962 7.666 10.193 7.666 2.459 0 4.792-.752 6.488-1.985.06-.042.115-.084.172-.126.057-.041.133-.092.194-.143 0 0 0-.018.014-.024.092-.075.191-.148.28-.22 1.645-1.505.699-4.268-1.511-4.56a2.646 2.646 0 00-2.52 1.103c-.643.838-1.752 1.31-3.27 1.31-2.602 0-4.139-1.616-4.258-4.14h12.163a1.833 1.833 0 001.832-1.832v-.454c0-6.354-3.863-10.14-9.738-10.14zm-4.258 8.6c.157-2.602 1.764-4.1 4.219-4.1 2.602 0 3.864 1.617 3.983 4.1H52.21z" fill="#fff"></path></svg>
      </>}
      {!params.row.user_extra_data?.itsme_registered && <>
        <PersonIcon sx={{
          color: !!params.row.check_profile ? 
          'green' : 
          (
            (!!params.row.check_profile_updated_at && (!params.row.check_profile_confirmed_at || (params.row.check_profile_updated_at > params.row.check_profile_confirmed_at))) ? 
            'gold' : 
            'red'
          )
        }} />
      </>}
      {!!params.row.extra_data?.itsme_confirmed && <>
        <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" style={{
          width: '24px',
          marginBottom: -0.5,
          color: !!params.row.check_borderel ? 
          'green' : 
          (
            (!!params.row.check_borderel_updated_at && (!params.row.check_borderel_confirmed_at || (params.row.check_borderel_updated_at > params.row.check_borderel_confirmed_at))) ? 
            'gold' : 
            'red'
          )
        }}><path d="M4.28 57.34C.91 53.97-.837 47.446.396 42.844l6.728-25.108c1.233-4.603 6.01-9.38 10.613-10.613L42.844.395C47.446-.838 53.97.91 57.34 4.28l18.38 18.38c3.37 3.362 5.116 9.893 3.894 14.495l-6.737 25.108c-1.233 4.603-6.01 9.38-10.613 10.613l-25.108 6.737C32.554 80.847 26.03 79.1 22.66 75.73L4.28 57.34z" fill="currentColor"></path><path d="M49.094 28.807c-2.389-.816-3.906-1.13-3.798-2.545.084-1.103.92-1.67 2.453-1.544 1.118.086 1.795.549 2.136 1.246a2.647 2.647 0 002.323 1.473c2.227.049 3.584-2.538 2.188-4.276-1.255-1.562-3.32-2.618-6.23-2.84-5.11-.393-8.33 1.892-8.63 5.782-.32 4.166 2.48 5.293 6.72 6.724 2.895.933 3.612 1.385 3.528 2.602-.09 1.18-1.125 1.765-2.697 1.652-1.31-.101-2.115-.62-2.494-1.398-.426-.882-1.38-1.37-2.354-1.39a2.617 2.617 0 00-1.947.786s-1.102 1.233-3.253 1.233c-1.563-.042-2.948-.883-2.948-3.072l.053-7.322a3.138 3.138 0 101.632-5.81 3.056 3.056 0 00-1.597.464l.004-2.689a1.717 1.717 0 00-1.715-1.726h-2.33a1.716 1.716 0 00-1.715 1.707l-.102 15.72c-.152 1.956-1.504 2.708-3.017 2.719-1.605 0-3.047-.84-3.047-3.072l.108-9.925a1.134 1.134 0 00-1.134-1.142h-3.487a1.134 1.134 0 00-1.133 1.125l-.11 11.327c-.04 4.721 3.68 6.845 8.803 6.845 2.144 0 3.615-.864 4.632-2.066 1.3 1.404 3.332 2.066 5.791 2.066 2.459 0 4.298-.322 6.124-1.358 1.27.747 2.89 1.215 4.852 1.365 5.306.408 8.535-1.985 8.853-6.122.277-3.69-2.495-5.17-6.462-6.54zM19.628 19.65a3.138 3.138 0 10-3.127-3.11 3.13 3.13 0 003.127 3.11zm36.84 23.117c-5.142 0-9.002 2.893-10.065 7.778-.29-4.576-3.209-7.32-7.511-7.32-3.002 0-4.965 1.348-6.118 3.156-1.31-2.03-3.502-3.156-6.235-3.156-2.536 0-10.544-.308-10.544 9.98v7.489a2.81 2.81 0 105.618 0v-8.226c0-2.386 1.347-4 3.579-4 2.04 0 3.23 1.422 3.23 3.97v8.271a2.81 2.81 0 005.62 0v-8.933c.22-2 1.462-3.309 3.462-3.309 2.155 0 3.309 1.423 3.309 3.97v8.273a2.81 2.81 0 105.617 0v-4.398c1.102 4.794 4.962 7.666 10.193 7.666 2.459 0 4.792-.752 6.488-1.985.06-.042.115-.084.172-.126.057-.041.133-.092.194-.143 0 0 0-.018.014-.024.092-.075.191-.148.28-.22 1.645-1.505.699-4.268-1.511-4.56a2.646 2.646 0 00-2.52 1.103c-.643.838-1.752 1.31-3.27 1.31-2.602 0-4.139-1.616-4.258-4.14h12.163a1.833 1.833 0 001.832-1.832v-.454c0-6.354-3.863-10.14-9.738-10.14zm-4.258 8.6c.157-2.602 1.764-4.1 4.219-4.1 2.602 0 3.864 1.617 3.983 4.1H52.21z" fill="#fff"></path></svg>
      </>}
      {!params.row.extra_data?.itsme_confirmed && <>
        <ReceiptIcon sx={{
          color: !!params.row.check_borderel ? 
          'green' : 
          (
            (!!params.row.check_borderel_updated_at && (!params.row.check_borderel_confirmed_at || (params.row.check_borderel_updated_at > params.row.check_borderel_confirmed_at))) ? 
            'gold' : 
            'red'
          )
        }} />
      </>}
      <EuroIcon sx={{
        color: !!params.row.check_payment ? 
        'green' : 
        (
          (!!params.row.check_payment_updated_at && (!params.row.check_payment_confirmed_at || (params.row.check_payment_updated_at > params.row.check_payment_confirmed_at))) ? 
          'gold' : 
          'red'
        )
      }} />
      <CheckCircleIcon sx={{
        color: (params.row.is_verified ? 'green' : 'red')
      }} />
      <EmailIcon sx={{
        color: (params.row.flag_email ? 'green' : 'red')
      }} />
    </> },
    { field: 'emails_last_created_at', headerName: 'Email', width: 140, valueFormatter: params => !!params?.value ? DateTime.fromISO(params?.value).toFormat("D") : '' },
    { field: 'activated', headerName: 'Activated', width: 120 },
    { field: 'handler', headerName: 'Handler', width: 240, renderCell: params => <>
      <Typography sx={{fontSize: '0.9rem'}}>{params.row.handler_full_name}</Typography>
    </> },
    { field: 'gift_expiry_date', headerName: 'KDO Expiry', width: 140, valueFormatter: params => !!params?.value ? DateTime.fromISO(params?.value).toFormat("D") : '' },
  ];
  
  return (
    <Page helper="emissionchecklist">

      <Box sx={{
        backgroundColor: theme.palette.quaternary?.main,
        padding: 1,
        position: 'relative',
        overflow: 'hidden',
        marginBottom: 2
      }}>
        <SearchIcon sx={{position: 'absolute', top: -8, left: -8, height: 100, width: 100, opacity: 0.2}} />
        <Box>
          <Grid container sx={{paddingRight: 1}} spacing={2}>
            <Grid item xs={4}>

            </Grid>
            <Grid item xs={4}>
              <Typography variant="h3" sx={{marginTop: 2}}>Emission Checklist</Typography>
            </Grid>
            <Grid item xs={4}>
              <StyledTextField
                label="Search..."
                sx={{width: '100%', "input": {backgroundColor: '#a9c2c0', padding: 1}}}
                value={search}
                id='search'
                onChange={(v) => {
                  setSearch(v);
                }}
                setEnterPressed={setEnterPressed}
              />
            </Grid>

            <Grid item container xs={4}>
              {/* <StyledButton
                label="Reset search"
                id='reset'
                onClick={(v) => {
                  // resetSearch();
                }}
                sx={{marginBottom: -4.5, marginLeft: 1}}
              /> */}
              <Grid item xs={5} sx={{padding: 0.5}}>
                {renderFilter(CardGiftcardIcon, 'Gifts', filterGifts, setFilterGifts, true, false)}
              </Grid>
            </Grid>
            <Grid item xs={4}>
              <StyledSelect
                label="Emission"
                value={searchEmission?.toString() || ''}
                id='emission_filter'
                onChange={(v) => {
                  setSearchEmission(parseInt(v, 10));
                }}
                list={emissions.map(emission => ({id: (emission.id || ''), name: [DateTime.fromISO(emission.start_date).toFormat("D"), DateTime.fromISO(emission.end_date).toFormat("D")].join(" - ")}))}
                sx={{backgroundColor: "#a9c2c0"}}
              />
            </Grid>
            <Grid item xs={4}>
              <StyledSelect
                label="Handler"
                value={searchHandler?.toString() || ''}
                id='handler_filter'
                onChange={(v) => {
                  setSearchHandler(parseInt(v, 10));
                }}
                list={[
                  {id: '99', name: 'Any order'},
                  {id: '0', name: 'Unhandled'},
                  ...handlers
                ]}
                sx={{backgroundColor: "#a9c2c0"}}
              />
            </Grid>

            <Grid item container xs={12}>
              <Grid item container xs={4}>
                <Grid item xs={6} sx={{padding: 0.5}}>
                  {renderFilter(PersonIcon, 'Profile', filterProfile, setFilterProfile, false, true)}
                </Grid>
                <Grid item xs={6} sx={{padding: 0.5}}>
                  {renderFilter(ReceiptIcon, 'Borderel', filterBorderel, setFilterBorderel, true, true)}
                </Grid>
              </Grid>
              <Grid item container xs={4}>
                <Grid item xs={6} sx={{padding: 0.5}}>
                  {renderFilter(EuroIcon, 'Payment', filterPayment, setFilterPayment, true, true)}
                </Grid>
                <Grid item xs={6} sx={{padding: 0.5}}>
                  {renderFilter(CheckCircleIcon, 'Verified', filterVerified, setFilterVerified, true, false)}
                </Grid>
              </Grid>
              <Grid item container xs={4}>
                <Grid item xs={6} sx={{padding: 0.5}}>
                  {renderFilter(EmailIcon, 'Email', filterEmail, setFilterEmail, true, false)}
                </Grid>
                <Grid item xs={6} sx={{padding: 0.5}}>
                  {renderFilter(DeleteForeverIcon, 'Deleted', filterDeleted, setFilterDeleted, true, false)}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <DataGrid
        action_count={1}
        actions={(params) => {
          return (<>
            <StyledIconButton title="Open Detail" color="primary"  onClick={() => {
              window.open(`/emission/checklist/${params.row.id}`, "_BLANK");
            }}>
              <VisibilityIcon />
            </StyledIconButton>
          </>);
        }}
        objectCount={objectsCount}
        getRowClassName={(params) => `datagrid-row-enabled-${!params.row.is_error}`}
        objects={(emissionKeys || []).map(u => ({
          ...u,
          vennoot_number: u.user_vennoot_number
        }))}
        columns={columns}
        loader={loader}
        onSortModelChange={(a, b) => {
          let a_0_field:string = a[0].field;
          if (a_0_field === 'name') a_0_field = `last_name ${a[0].sort}, first_name`;
          setOrder([a_0_field, a[0].sort].join(' '));
        }}
        page={page}
        setPage={(p) => {setPage(p);}}
      />
    </Page>
  );
}
